import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Trophy, Users, LogOut, User, Menu, X, CreditCard, Activity, DollarSign, Wallet } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useUser } from '../../hooks/useUser';
import { PublicFooter } from './PublicFooter';
import { SEOMetaTags } from '../shared/SEOMetaTags';

const navigation = [
  { name: 'Tableau de bord', href: '/client', icon: LayoutDashboard },
  { name: 'Candidats', href: '/client/contestants', icon: Users },
  { name: 'Transactions', href: '/client/transactions', icon: DollarSign },
  { name: 'Retraits', href: '/client/withdrawals', icon: Wallet },
];

interface ClientLayoutProps {
  children: React.ReactNode;
}

export const ClientLayout: React.FC<ClientLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { user } = useUser();
  const [isOpen, setIsOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/client/login');
  };

  return (
    <div className="min-h-screen bg-black">
      <SEOMetaTags
        title="Espace Client - VotAfrica"
        description="Gérez vos concours et suivez les votes de vos candidats en temps réel sur VotAfrica."
      />
      <nav className="fixed top-0 z-50 w-full backdrop-blur-md bg-black/50 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo et Navigation */}
            <div className="flex items-center flex-1">
              <Link to="/client" className="flex-shrink-0 relative group">
                <div className="absolute -inset-3 bg-gradient-to-r from-yellow-600 via-yellow-400 to-yellow-600 rounded-full blur-sm opacity-70 group-hover:opacity-75 transition"></div>
                <img 
                  src="https://pgnkuxeckqnzkyfcrwzs.supabase.co/storage/v1/object/public/candidates/logo_66666666-6666-6666-6666-666666666666.jpg"
                  alt="Logo VotAfrica"
                  className="relative h-16 w-16 rounded-full object-cover"
                />
              </Link>

              {/* Titre et slogan */}
              <div className="ml-6 hidden lg:block">
                <h1 className="text-xl font-bold">
                  <span className="bg-gradient-to-r from-yellow-200 via-yellow-400 to-yellow-200 text-transparent bg-clip-text">
                    VotAfrica
                  </span>
                </h1>
                <p className="text-xs text-gray-400">Espace Client</p>
              </div>

              {/* Navigation principale - Desktop */}
              <div className="hidden md:block ml-10">
                <div className="flex items-center space-x-4">
                  {navigation.map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`
                          px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition-all
                          ${isActive 
                            ? 'bg-gradient-to-r from-yellow-400/20 to-yellow-600/20 text-yellow-400 shadow-lg shadow-yellow-500/20' 
                            : 'text-gray-300 hover:text-yellow-400 hover:bg-white/5'
                          }
                        `}
                      >
                        <item.icon className="h-4 w-4" />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Profil et Déconnexion - Desktop */}
            <div className="hidden md:flex items-center space-x-4">
              <Link
                to="/client/profile"
                className={`
                  px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition-all
                  ${location.pathname === '/client/profile'
                    ? 'bg-gradient-to-r from-yellow-400/20 to-yellow-600/20 text-yellow-400 shadow-lg shadow-yellow-500/20'
                    : 'text-gray-300 hover:text-yellow-400 hover:bg-white/5'
                  }
                `}
              >
                <User className="h-4 w-4" />
                <span className="hidden sm:inline">{user?.name}</span>
              </Link>

              <button
                onClick={handleLogout}
                className="px-4 py-2 rounded-xl text-sm font-medium text-gray-300 hover:text-yellow-400 hover:bg-white/5 flex items-center gap-2 transition-all"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Déconnexion</span>
              </button>
            </div>

            {/* Menu mobile */}
            <div className="md:hidden">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="p-2 rounded-xl text-gray-300 hover:text-yellow-400 hover:bg-white/5"
              >
                {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Menu mobile - Panel */}
        {isOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`
                      block px-3 py-2 rounded-xl text-base font-medium flex items-center gap-2
                      ${isActive
                        ? 'bg-gradient-to-r from-yellow-400/20 to-yellow-600/20 text-yellow-400'
                        : 'text-gray-300 hover:text-yellow-400 hover:bg-white/5'
                      }
                    `}
                    onClick={() => setIsOpen(false)}
                  >
                    <item.icon className="h-5 w-5" />
                    {item.name}
                  </Link>
                );
              })}
              <Link
                to="/client/profile"
                className="block px-3 py-2 rounded-xl text-base font-medium text-gray-300 hover:text-yellow-400 hover:bg-white/5 flex items-center gap-2"
                onClick={() => setIsOpen(false)}
              >
                <User className="h-5 w-5" />
                Profil
              </Link>
              <button
                onClick={() => {
                  setIsOpen(false);
                  handleLogout();
                }}
                className="w-full text-left px-3 py-2 rounded-xl text-base font-medium text-gray-300 hover:text-yellow-400 hover:bg-white/5 flex items-center gap-2"
              >
                <LogOut className="h-5 w-5" />
                Déconnexion
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Contenu principal */}
      <main className="flex-1 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {children}
        </div>
      </main>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
};
